import React, { useRef, useEffect, useState, useMemo } from 'react';
import ResultCard from './ResultCard';
import useScrollHandlers from './UseScrollHandler';
import { searchInsurance } from '../services/api/AiSearchService';
import { searchCondAtom } from '../store/insuranceSearchStore';
import { useAtom } from 'jotai';
import { trackEvent } from '../services/api/TrackingService';
import { v4 as uuidv4 } from 'uuid';
import { DOCUMENT_TYPE } from '../utils/insuranceTypeUtils';

const DOC_TYPE_CSS_CLASS = {
  'しおり・約款':         'doc-type-tag--policy-booklet',
  '新契約関連':           'doc-type-tag--new-contract',
  '保全・収納・支払関連': 'doc-type-tag--maintenance',
  'ハンドブック':         'doc-type-tag--handbook',
};

const DOC_TYPE_ORDER = ['しおり・約款', '新契約関連', '保全・収納・支払関連', 'ハンドブック'];

const CompanyResultSection = ({ company, handleCardClick, isMobile }) => {
  const scrollRef = useRef(null);
  const scrollHandlers = useScrollHandlers(company.id);
  const [searchCond] = useAtom(searchCondAtom);

  const [results, setResults] = useState(company.results);

  // 追加: 2行以上検知とアコーディオン制御
  const productSelectorRef = useRef(null);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [firstLineProducts, setFirstLineProducts] = useState([]);
  const [remainingProducts, setRemainingProducts] = useState([]);

  const products = useMemo(
    () => (company.productCandidates || []).filter(candidate => candidate.name).map(candidate => candidate.name),
    [company.productCandidates]
  );

  // 検索条件の文書種別が「all」であるかどうかを判定
  const isDocTypeAll = searchCond.document_type_code === DOCUMENT_TYPE.ALL;

  /**
   * 商品名ごとに関連する文書種別のラベルをセットで保持するマップを作成
   */
  const productDocTypeMap = useMemo(() => {
    const map = {};

    if (!isDocTypeAll) {
      return map;
    }

    // 商品ごとに文書種別ラベル(バックエンドで重複排除済み)を定義した順序でソートして保持
    (company.productCandidates || []).forEach(candidate => {
      if (!candidate.name || !candidate.classifications?.length) return;
      map[candidate.name] = [...candidate.classifications].sort(
        (a, b) => DOC_TYPE_ORDER.indexOf(a) - DOC_TYPE_ORDER.indexOf(b)
      );
    });

    return map;
  }, [company.productCandidates, isDocTypeAll]);

  // 検索結果の有無を判定を1箇所で制御
  const hasResults = results && results.length > 0;

  // Handle scroll with left/right buttons
  const handleScroll = (direction) => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      // Calculate scroll amount based on card width
      const cardWidth = 280 + 15; // card width + margin
      const scrollAmount = cardWidth * 4; // scroll 4 cards at once

      const newScrollLeft = direction === 'left'
        ? scrollContainer.scrollLeft - scrollAmount
        : scrollContainer.scrollLeft + scrollAmount;

      scrollContainer.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // パス文字列を解析する関数
  const parsePathString = (pathString) => {
    // パスをスラッシュで分割
    const segments = pathString.split('/');

    // 会社情報（最初のセグメント）から数字とアンダースコアを除去
    let company = null;
    if (segments.length > 0) {
      const companyMatch = segments[0].match(/\d+_(.+)/);
      company = companyMatch ? companyMatch[1] : segments[0];
    }

    // ファイル名（最後のセグメント）
    const file = segments.length > 2 ? segments[segments.length - 1] : null;

    // タイトル（ファイル名から拡張子を除いた部分）
    let title = null;
    if (file) {
      // 最後の「.」以降を拡張子として扱い、それを除いた部分をタイトルとする
      const lastDotIndex = file.lastIndexOf('.');
      if (lastDotIndex === -1) {
        // 拡張子がない場合はファイル名全体をタイトルとする
        title = file;
      } else {
        // 拡張子を除いたファイル名部分をタイトルとする
        title = file.substring(0, lastDotIndex);
      }
    }
    return {
      company,
      title
    };
  };

  // Helper function to format API results to match UI expected structure
  const formatSearchResults = (apiResults) => {
    // Group results by company
    const resultsByCompany = {};

    let idCounter = 0;

    // Process the search results from the API
    if (apiResults && apiResults.results) {
      apiResults.results.forEach(item => {
        const { company, title } = parsePathString(item.blob_name);

        if (!resultsByCompany[company]) {
          resultsByCompany[company] = {
            id: company,
            company: company,
            results: []
          };
        }

        resultsByCompany[company].results.push({
          id: idCounter++, // 各結果に一意のIDを付与
          title: title || 'タイトルがありません',
          pages: item.pages,
          similarity: Math.round(item.score) || 0, // Convert score to percentage
          pdfUrl: item.blob_name || 'PDF URLがありません',
          summary: item.summary || '要約がありません',
          classification: `【${item.classification}】` || '分類がありません',
          classificationLabel: item.classification || '',
          productName: item.product_name || '商品名がありません',
          creationDate: item.creation_date || '作成日がありません',
          indexName: item.index_name, // AIチャット用: 表示中文書の検索インデックス名
        });
      });
    }

    // Convert to array and sort by company name
    return Object.values(resultsByCompany).sort((a, b) => a.company.localeCompare(b.company));
  };

  // 2行以上になっているかをチェック
  useEffect(() => {
    const checkMultiLine = () => {
      if (productSelectorRef.current) {
        // 測定用の一時的なコンテナを作成
        const tempContainer = document.createElement('div');
        const containerWidth = productSelectorRef.current.getBoundingClientRect().width;
        tempContainer.style.cssText = `
          position: absolute;
          visibility: hidden;
          width: ${containerWidth}px;
          display: flex;
          flex-wrap: wrap;
          gap: 5px 10px;
          align-items: center;
        `;

        // 全商品の要素を複製して追加
        products.forEach(product => {
          const label = document.createElement('label');
          label.className = 'product-checkbox-label checkbox-native-box--small';
          label.style.cssText = 'display: flex; align-items: center; gap: 5px; white-space: nowrap; line-height: 2;';

          // チェックボックスとテキストを追加（正確なサイズ計算のため）
          const checkbox = document.createElement('input');
          checkbox.type = 'checkbox';
          checkbox.className = 'checkbox-native--small';
          label.appendChild(checkbox);

          const text = document.createTextNode(product);
          label.appendChild(text);

          if (isDocTypeAll && productDocTypeMap[product]) {
            const tagsSpan = document.createElement('span');
            tagsSpan.className = 'doc-type-tags';
            productDocTypeMap[product].forEach(docLabel => {
                const tag = document.createElement('span');
                tag.className = `doc-type-tag ${DOC_TYPE_CSS_CLASS[docLabel] || ''}`;
                tag.textContent = docLabel;
                tagsSpan.appendChild(tag);
              });
            label.appendChild(tagsSpan);
          }

          tempContainer.appendChild(label);
        });

        productSelectorRef.current.appendChild(tempContainer);

        // 測定
        const children = Array.from(tempContainer.children);

        if (children.length > 1) {
          const firstChildTop = children[0].getBoundingClientRect().top;
          const lastChildTop = children[children.length - 1].getBoundingClientRect().top;

          // 2行以上あるかチェック（より厳密に）
          if (Math.abs(lastChildTop - firstChildTop) > 10) {
            // 1行目に収まる要素を特定
            const firstLine = [];
            const remaining = [];

            children.forEach((child, index) => {
              const childTop = child.getBoundingClientRect().top;
              if (Math.abs(childTop - firstChildTop) < 10) {
                firstLine.push(products[index]);
              } else {
                remaining.push(products[index]);
              }
            });

            setFirstLineProducts(firstLine);
            setRemainingProducts(remaining);
            setIsMultiLine(true);
          } else {
            // 1行のみの場合
            setIsMultiLine(false);
            setFirstLineProducts(products);
            setRemainingProducts([]);
          }
        } else {
          setIsMultiLine(false);
          setFirstLineProducts(products);
          setRemainingProducts([]);
        }

        // 測定用コンテナを削除
        productSelectorRef.current.removeChild(tempContainer);
      }
    };

    const timer = setTimeout(checkMultiLine, 100);

    window.addEventListener('resize', checkMultiLine);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkMultiLine);
    };
  }, [products, isDocTypeAll]);

  const onSearch = async () => {
    let newCond = {};
    if (selectedProducts.length === 0) {
      newCond = { ...searchCond };
    } else {
      newCond = { ...searchCond, products: selectedProducts };
    }
    newCond.companies = [company.index];

    const queryId = uuidv4();

    trackEvent({
      name: "InsuranceProductFilterExecuted",
      properties: {
        queryId: queryId,
        ...newCond
      }
    }).catch((error) => console.error('検索実行ログ送信エラー:', error))

    try {
      const response = await searchInsurance(newCond);
      const formattedResults = formatSearchResults(response)[0]?.results?.map(result => ({ ...result, queryId })) || [];
      setResults(formattedResults);

      trackEvent({
        name: 'InsuranceProductFilterResult',
        properties: {
          queryId: queryId,
          resultJson: JSON.stringify(formattedResults)
        }
      }).catch((error) => console.error('検索結果ログ送信エラー:', error))

    } catch (error) {
      trackEvent({
        name: 'InsuranceProductFilterFailed',
        properties: {
          queryId: queryId,
          ...newCond,
          errorMessage: error.message
        }
      }).catch((error) => console.error('検索失敗ログ送信エラー:', error));
    }
  };

  const productSelecthandler = (e) => {
    const value = e.target.value;
    if (e.target.checked) {
      setSelectedProducts(prev => [...prev, value]);
    } else {
      setSelectedProducts(prev => prev.filter(p => p !== value));
    }
  };

  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  const handleMouseEnter = () => {
    if (!isMobile && isMultiLine) {
      setIsAccordionOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsAccordionOpen(false);
    }
  };

  return (
    <div className="company-section">
      <div className="company-header align-top">
        <div>
          <h3 className="company-name">{company.company}</h3>
        </div>
        {hasResults && (
          <>
            <div
              className='product-searcher'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="product-filter-header">
                <span className="filter-label">商品で絞り込み：</span>
                <div className="product-selector-wrapper">
                  <div
                    className={`product-selector-inline ${isMultiLine ? 'multi-line' : ''}`}
                    ref={productSelectorRef}
                  >
                    {/* 初回レンダリング時は全商品表示して高さ測定 */}
                    {products.map((product, index) => (
                      <label
                        key={index}
                        className="product-checkbox-label checkbox-native-box--small"
                        style={isMultiLine && !firstLineProducts.includes(product) ? { display: 'none' } : {}}
                      >
                        <input
                          type="checkbox"
                          className="checkbox-native--small"
                          value={product}
                          onChange={productSelecthandler}
                          checked={selectedProducts.includes(product)}
                        />
                        <span className='checkbox-native--small-text'>{product}</span>
                        {isDocTypeAll && productDocTypeMap[product] && (
                          <span className="doc-type-tags">
                            {productDocTypeMap[product].map(label => (
                                <span key={label} className={`doc-type-tag ${DOC_TYPE_CSS_CLASS[label] || ''}`}>
                                  {label}
                                </span>
                              ))}
                          </span>
                        )}
                      </label>
                    ))}
                    {/* 他◯件を常に表示 */}
                    {isMultiLine && remainingProducts.length > 0 && (
                      <span className="remaining-count-02">
                        他{remainingProducts.length}件
                      </span>
                    )}
                    {isMultiLine && (
                      <button
                        className={`expand-button-filter ${isAccordionOpen ? 'open' : ''}`}
                        onClick={toggleAccordion}
                        aria-label="商品一覧を展開"
                        type="button"
                      >
                      </button>
                    )}
                  </div>

                  {/* 2行目以降: hover時に表示 */}
                  {isMultiLine && remainingProducts.length > 0 && (
                    <div className={`accordion-content-filter ${isAccordionOpen ? 'open' : ''}`}>
                      <div className='product-selector'>
                        {remainingProducts.map((product, index) => (
                          <div key={index}>
                            <label className='product-checkbox-label checkbox-native-box--small'>
                              <input
                                type="checkbox"
                                className='checkbox-native--small'
                                value={product}
                                onChange={productSelecthandler}
                                checked={selectedProducts.includes(product)}
                              />
                              <span className='checkbox-native--small-text'>{product}</span>
                              {isDocTypeAll && productDocTypeMap[product] && (
                                <span className="doc-type-tags">
                                  {productDocTypeMap[product].map(label => (
                                      <span key={label} className={`doc-type-tag ${DOC_TYPE_CSS_CLASS[label] || ''}`}>
                                        {label}
                                      </span>
                                    ))}
                                </span>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='product-button-container'>
              <button className='search-button-filter' onClick={onSearch}>
                絞り込み選択
              </button>
            </div>
          </>
        )}
      </div>
      
      {hasResults ? (
        <div className="scroll-nav-wrapper">
          <div
            className={`results-grid ${isMobile ? 'mobile' : ''}`}
            ref={scrollRef}
            onTouchStart={(e) => scrollHandlers.handleTouchStart(e, scrollRef.current)}
            onTouchMove={(e) => scrollHandlers.handleTouchMove(e, scrollRef.current)}
            onTouchEnd={() => scrollHandlers.handleTouchEnd(scrollRef.current)}
            onMouseDown={(e) => scrollHandlers.handleMouseDown(e, scrollRef.current)}
            onMouseMove={(e) => scrollHandlers.handleMouseMove(e, scrollRef.current)}
            onMouseUp={() => scrollHandlers.handleMouseUp(scrollRef.current)}
            onMouseLeave={() => scrollHandlers.handleMouseLeave(scrollRef.current)}
          >
            <div className="scroll-indicator"></div>
            <div className="swipe-hint">← スワイプしてスクロール →</div>

            {results.map((result) => (
              <ResultCard
                key={result.id}
                result={result}
                handleCardClick={handleCardClick}
                isDragging={scrollHandlers.isDragging}
                companyId={company.id}
              />
            ))}
          </div>
          <div className="scroll-nav">
            <button
              className="scroll-button scroll-button-left"
              onClick={() => handleScroll('left')}
              aria-label="左にスクロール"
            >
              ◀
            </button>
            <button
              className="scroll-button scroll-button-right"
              onClick={() => handleScroll('right')}
              aria-label="右にスクロール"
            >
              ▶
            </button>
          </div>
        </div>
      ) : (
        <div className="no-results-message">
          <p>検索条件にHITする文書が存在しないため、検索結果が見つかりませんでした(0件)</p>
        </div>
      )}
    </div>
  );
};

export default CompanyResultSection;